import { useState, useEffect } from "react";
import { m } from "framer-motion";
import {
  Paintbrush, Clock, ShieldCheck, Award, DollarSign, BadgeCheck,
  Check, Star, Sparkles, ArrowRight, MapPin, CheckCircle2, Quote,
  Hammer, CalendarCheck, MessageCircle, PaintRoller, Phone,
  Home, Building2, PaintBucket,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import QuoteForm from "@/components/QuoteForm";
import QuoteModal from "@/components/QuoteModal";
import SoCalMap from "@/components/SoCalMap";
import { useContent } from "@/context/ContentContext";
import { pushEvent } from "@/lib/api";
import babImg from "@/assets/images/bab.png";
import bag2Img from "@/assets/images/bag2.png";
import cslbImg from "@/assets/images/cslb.png";

const BADGES = [
  { icon: Paintbrush, label: "Experts in interiors & exteriors" },
  { icon: Clock, label: "Fast turnarounds (1–2 days)" },
  { icon: ShieldCheck, label: "CSLB certified & insured" },
  { icon: Award, label: "5 year warranty on all jobs" },
  { icon: DollarSign, label: "Best pricing guaranteed" },
  { icon: BadgeCheck, label: "EPA certified for lead paint" },
];

const HERO_SERVICES = [
  { icon: Home, label: "Residential Interior & Exterior Painting Services" },
  { icon: Building2, label: "Commercial Painting for Offices, Buildings & Businesses" },
  { icon: PaintBucket, label: "Stucco, Texture, Coating, Finish Repair & Application" },
];

function HeroServices({ className = "", testid, spanClassName = "font-semibold", spanStyle }) {
  return (
    <ul className={`flex flex-col gap-4 ${className}`} data-testid={testid}>
      {HERO_SERVICES.map((s, i) => {
        const Icon = s.icon;
        return (
          <li key={i} className="flex items-center gap-3" data-testid={`hero-service-item-${i}`}>
            <Icon className="h-7 w-7 shrink-0 text-[#2563EB]" strokeWidth={2} />
            <span className={`text-base text-black sm:text-lg ${spanClassName}`} style={spanStyle}>{s.label}</span>
          </li>
        );
      })}
    </ul>
  );
}

const VALUE_PROPS = [
  { icon: PaintRoller, text: "Perfect prep for a flawless finish & lasting results" },
  { icon: CalendarCheck, text: "Respectful crews and reliable scheduling" },
  { icon: Award, text: "Excellent quality from estimate to finish" },
  { icon: ShieldCheck, text: "Careful protection and cleanup of your home" },
  { icon: DollarSign, text: "Fair pricing without compromising quality" },
  { icon: MessageCircle, text: "Clear communication every step of the way" },
];

const INTERIOR = [
  "Thorough prep and surface repair to hide dings, cracks, and wear — rooms look clean and truly refreshed",
  "Your floors, furniture, and nearby surfaces fully protected from start to finish",
  "Crisp edges, uniform coverage, and careful detail — the finish your home deserves",
  "Respectful crews and daily cleanup keep your home neat and ready for normal life each evening",
];

const EXTERIOR = [
  "We prep and repair the right way so paint bonds better, looks smoother, and lasts longer",
  "High-performance coatings preserve your home's beauty through heat, sun, wind & rain",
  "Careful protection for landscaping, concrete, fixtures, and surrounding areas",
  "Clean lines and even coverage that give your home a sharp, well-kept, long-lasting look",
];

const REVIEWS = [
  { name: "Julie Nunez", when: "2 days ago", text: "We just moved in and needed several rooms repainted. ColorStone was fast, super polite, and left everything spotless. Really happy with the color and the finish!" },
  { name: "Randy Henrickson", when: "1 week ago", text: "I usually don't give 5 stars, but these guys earned it. 100% happy with the work — I especially enjoyed the attention to finishing every little detail with great care." },
  { name: "Robert Baksh", when: "3 weeks ago", text: "Every job is unique but the whole thing went fast and went great. The crew respected my home and property throughout. Would highly recommend to anyone." },
];

const COMP_SERVICES = [
  "Bathroom & kitchen renovation", "Carpentry & wood work", "Construction & remodeling",
  "Drywall repair & install", "Electrical work & solar panels", "Flooring (all kinds of floors)",
  "Interior & exterior finish repair", "Insulation", "Landscaping", "Concrete work", "Plumbing", "Roofing",
];

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.08 } }),
};

function Section({ children, className = "", id }) {
  return (
    <section id={id} className={className}>
      <div className="mx-auto max-w-7xl px-5 lg:px-8">{children}</div>
    </section>
  );
}

function Overline({ children, className = "" }) {
  return (
    <span className={`text-sm font-bold uppercase tracking-[0.2em] text-[#2563EB] ${className}`}>
      {children}
    </span>
  );
}

// PC: card invades 50% into the image (both axes). Mobile: offset-overlap card.
// PC: card overlaps 50% of the square image (both axes). Interior image LEFT, exterior image RIGHT.
// Mobile: square image on top, card pulled up and slightly WIDER than the image.
const CTA_CLS = "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-[#2563EB] px-6 py-4 text-sm font-bold text-white shadow-[4px_5px_16px_rgba(249,115,22,0.55)] transition-transform duration-200 hover:bg-[#1D4ED8] hover:scale-105 sm:px-7 sm:py-4 sm:text-lg";

function Caption({ text }) {
  if (!text) return null;
  const marker = "— ";
  const idx = text.indexOf(marker);
  if (idx === -1) return <>{text}</>;
  return (
    <>
      {text.slice(0, idx + marker.length)}
      <strong className="font-bold">{text.slice(idx + marker.length)}</strong>
    </>
  );
}

function ServiceBlock({ id, overline, title, titleNode, intro, features, img, imgSrcSet, reverse, onQuote, ctaLabel, ctaTestId }) {
  return (
    <div className="relative lg:pt-[26%]" data-testid={`service-${id}`}>
      <img
        src={img}
        srcSet={imgSrcSet}
        sizes="(min-width: 1024px) 50vw, 100vw"
        alt={title}
        loading="lazy"
        className={`aspect-square w-full rounded-2xl object-cover shadow-lg lg:absolute lg:top-0 lg:w-[50%] ${
          reverse ? "lg:right-0" : "lg:left-0"
        }`}
      />
      <m.div
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.3 }}
        className={`relative z-10 -mt-14 rounded-2xl border-black bg-white p-4 shadow-2xl sm:p-8 lg:mt-0 lg:w-[75%] lg:border lg:p-10 ${
          reverse ? "" : "lg:ml-auto"
        }`}
      >
        <Overline>{overline}</Overline>
        <h3 className="font-heading mt-2 text-3xl font-extrabold tracking-tight text-[#0F172A] lg:text-4xl">
          {titleNode || title}
        </h3>
        <p className="mt-3 text-lg leading-relaxed text-slate-600">{intro}</p>
        <ul className="mt-6 grid gap-x-5 gap-y-4 sm:grid-cols-2">
          {features.map((f, i) => (
            <li key={i} className="flex gap-2.5">
              <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-[#2563EB]" />
              <span className="text-base leading-snug text-slate-600">{f}</span>
            </li>
          ))}
        </ul>
        <div className="mt-7 flex flex-wrap gap-3">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-[#0F172A] px-3.5 py-1.5 text-sm font-bold text-white">
            <Award className="h-4 w-4 text-[#2563EB]" /> 5 Year Workmanship Warranty
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-[#0F172A] px-3.5 py-1.5 text-sm font-bold text-white">
            <ShieldCheck className="h-4 w-4 text-[#2563EB]" /> 100% Satisfaction Guarantee
          </span>
        </div>
        {ctaLabel && (
          <button onClick={onQuote} data-testid={ctaTestId} className={`mt-7 w-full sm:w-auto ${CTA_CLS}`}>
            {ctaLabel} <ArrowRight className="h-5 w-5 text-[#F97316]" />
          </button>
        )}
      </m.div>
    </div>
  );
}

function CredibilityBand() {
  return (
    <div className="grid grid-cols-2 gap-4 rounded-2xl border border-[#E2E8F0] bg-white p-6 md:grid-cols-4">
      <div className="flex flex-col items-center gap-2 text-center md:flex-row md:text-left">
        <img src={babImg} alt="BBB Accredited A+" className="h-14 w-14 rounded-lg object-cover md:hidden" />
        <span className="hidden h-14 w-14 items-center justify-center rounded-lg bg-[#0F172A] font-heading text-xl font-black text-white md:flex">A+</span>
        <div>
          <p className="whitespace-nowrap text-sm font-bold text-[#0F172A] md:whitespace-normal md:text-base">BBB Accredited</p>
          <p className="text-sm text-slate-500">A+ Rating</p>
        </div>
      </div>
      <div className="flex flex-col items-center gap-2 text-center md:flex-row md:text-left">
        <img src={bag2Img} alt="Google rating" className="h-14 w-14 rounded-lg object-cover md:hidden" />
        <span className="hidden font-heading text-4xl font-black text-[#0F172A] md:block">4.9</span>
        <div>
          <div className="flex items-center justify-center gap-1.5 md:justify-start">
            <span className="text-sm font-bold text-[#0F172A] md:hidden">4.9</span>
            <div className="flex">
              {[...Array(5)].map((_, i) => (<Star key={i} className="h-4 w-4 text-[#F59E0B]" fill="#F59E0B" />))}
            </div>
          </div>
          <p className="text-sm text-slate-500">Google rating</p>
        </div>
      </div>
      <div className="flex flex-col items-center gap-2 text-center md:flex-row md:text-left">
        <Award className="h-11 w-11 text-[#2563EB]" />
        <div>
          <p className="whitespace-nowrap text-sm font-bold text-[#0F172A] md:whitespace-normal md:text-base">ThreeBest Rated</p>
          <p className="text-sm text-slate-500">Top 3 Painters</p>
        </div>
      </div>
      <div className="flex flex-col items-center gap-2 text-center md:flex-row md:text-left">
        <BadgeCheck className="h-11 w-11 text-[#2563EB]" />
        <div>
          <p className="whitespace-nowrap text-sm font-bold text-[#0F172A] md:whitespace-normal md:text-base">100% Satisfaction</p>
          <p className="text-sm text-slate-500">Guaranteed</p>
        </div>
      </div>
    </div>
  );
}

export default function Landing() {
  const [modal, setModal] = useState(false);
  const openQuote = () => setModal(true);
  const { img, imgSrcSet, carousel, reviewCards, content, resolve } = useContent();
  const marquee = [...carousel, ...carousel];

  // iOS WebKit bug: images that finish decoding AFTER a GPU-composited animating
  // layer (the marquee) has painted are not flushed to screen — they stay blank or
  // half-drawn until a tab switch forces a repaint. Fix: decode every marquee image
  // in JS first, then mount the strip with decoding="sync" so bitmaps are ready at paint.
  const carouselKey = carousel.map((c) => c.resolved).join("|");
  const [carouselReady, setCarouselReady] = useState(false);
  useEffect(() => {
    let cancelled = false;
    const urls = carouselKey.split("|").filter(Boolean);
    // No real images yet (content still loading) — keep the strip gated; nothing to show.
    if (!urls.length) return;
    // New set of real images arrived: gate again until they are decoded, so they never
    // mount blank into the composited layer on iOS.
    setCarouselReady(false);
    const done = () => { if (!cancelled) setCarouselReady(true); };
    Promise.all(
      urls.map((u) => {
        const im = new Image();
        im.src = u;
        return im.decode ? im.decode().catch(() => {}) : Promise.resolve();
      })
    ).then(done);
    const t = setTimeout(done, 2500); // never block the UI on a slow image
    return () => { cancelled = true; clearTimeout(t); };
  }, [carouselKey]);
  const phone = content.phone || "(310) 508-6563";
  const telHref = `tel:${phone.replace(/[^0-9+]/g, "")}`;

  return (
    <div id="top" className="bg-[#FFFBF5]">
      <Navbar />
      <QuoteModal open={modal} onClose={() => setModal(false)} />

      <main>
      {/* ================= HERO ================= */}
      <section className="relative overflow-hidden pt-28 pb-14 lg:pt-36 lg:pb-20">
        {/* very light decorative background */}
        <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
          {(img("hero_bg") || img("hero")) && (
            <img
              src={img("hero_bg") || img("hero")}
              alt=""
              className="absolute inset-0 h-full w-full scale-110 object-cover opacity-[0.16] blur-[2px] [mask-image:radial-gradient(120%_110%_at_72%_18%,#000_0%,transparent_78%)]"
            />
          )}
          <div className="absolute -left-24 -top-24 h-96 w-96 rounded-full bg-[#2563EB]/10 blur-3xl" />
          <div className="absolute right-0 top-20 h-[420px] w-[420px] rounded-full bg-[#0F172A]/[0.06] blur-3xl" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#FFFBF5]/70 via-[#FFFBF5]/35 to-[#FFFBF5]" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#FFFBF5] via-[#FFFBF5]/50 to-transparent lg:via-[#FFFBF5]/25" />
        </div>

        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="grid items-center gap-10 lg:grid-cols-2 lg:gap-14">
            <m.div variants={fadeUp} initial="hidden" animate="show">
              <Overline>Licensed · Insured · Guaranteed</Overline>
              <h1 className="font-heading mt-3 text-4xl font-black leading-[1.05] tracking-tight text-[#0F172A] sm:text-5xl lg:text-6xl">
                Interior &<br className="md:hidden" /> Exterior Painting
                <span className="mt-2 block text-[#2563EB]">Done right the first time.</span>
              </h1>
              <p className="mt-5 max-w-lg text-xl leading-relaxed text-slate-600">
                Bring new life to your <em className="italic">home</em> or <em className="italic">business</em> with professional painting that{" "}
                <span className="font-bold text-[#0F172A]">stands up to everyday life</span>. We deliver
                clean lines, lasting color & a finish you'll feel proud of every day.
              </p>
              {/* Desktop/tablet: services sit right under the intro paragraph */}
              <HeroServices className="mt-6 hidden md:flex" testid="hero-services-list" />
              <div className="mt-7 flex flex-col items-start gap-3 pb-[0.5rem] md:pb-0">
                <button onClick={openQuote} data-testid="hero-cta-btn" className={CTA_CLS}>
                  Get a Free Quote <ArrowRight className="h-5 w-5 text-[#F97316]" />
                </button>
                <div className="flex items-center gap-2">
                  <div className="flex">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="h-5 w-5 text-[#F59E0B]" fill="#F59E0B" />
                    ))}
                  </div>
                  <span className="text-sm font-semibold text-[#1E293B] sm:text-base">4.9 · 200+ homeowners</span>
                </div>
                <div className="flex items-center gap-1.5" data-testid="hero-service-area">
                  <MapPin className="h-4 w-4 text-[#F97316] sm:h-5 sm:w-5" fill="#F97316" />
                  <span className="text-sm font-bold text-[#0F172A] sm:text-base">Southern California</span>
                </div>
              </div>
              {/* Mobile only: services sit below the Southern California text */}
              <HeroServices className="mt-6 flex md:hidden" testid="hero-services-list-mobile" spanClassName="" spanStyle={{ fontWeight: 440 }} />
            </m.div>

            <m.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.15 }} className="relative">
              <img src={img("hero")} srcSet={imgSrcSet("hero")} sizes="(min-width: 1024px) 600px, 300px" alt="Freshly painted house exterior" fetchPriority="high" width="1200" height="675" className="h-56 w-full rounded-2xl object-cover shadow-lg sm:h-72" />
              <div className="relative z-10 mx-2 -mt-12 rounded-2xl border-2 border-[#F97316] bg-white p-6 shadow-2xl sm:mx-6 sm:p-7">
                <div className="mb-2 flex items-center gap-1.5 text-sm font-bold text-[#0F172A]" data-testid="hero-form-service-area">
                  <MapPin className="h-4 w-4" fill="currentColor" /> Southern California
                </div>
                <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#F97316]">Free Estimate</p>
                <h2 className="font-heading mt-1 text-2xl font-extrabold text-[#0F172A]">Claim Your FREE Quote</h2>
                <p className="mb-5 mt-1 text-base text-slate-500">Takes 30 seconds. No obligation.</p>
                <QuoteForm variant="light" testPrefix="hero" />
              </div>
            </m.div>
          </div>
        </div>
      </section>

      {/* ================= 6 FEATURE CARDS (3x2 PC, 1/row mobile) ================= */}
      <Section className="pb-8">
        <div className="grid grid-cols-1 gap-2.5 md:grid-cols-3 md:gap-4">
          {BADGES.map((b, i) => {
            const Icon = b.icon;
            return (
              <m.div
                key={i}
                variants={fadeUp}
                custom={i}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                className="flex items-center gap-4 rounded-2xl px-2 py-2.5 transition-transform duration-200 md:bg-[#0F172A] md:px-6 md:py-6 md:shadow-lg md:hover:-translate-y-1"
                data-testid={`feature-card-${i}`}
              >
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border-2 border-[#F97316] bg-[#2563EB] md:h-14 md:w-14">
                  <Icon className="h-6 w-6 text-white md:h-7 md:w-7" />
                </span>
                <span className="text-lg font-semibold leading-tight text-[#0F172A] md:font-bold md:text-white">{b.label}</span>
              </m.div>
            );
          })}
        </div>
      </Section>

      {/* ================= CREDIBILITY MINI (2/row mobile) ================= */}
      {/* Desktop / tablet */}
      <Section className="hidden py-8 md:block">
        <CredibilityBand />
      </Section>

      {/* Mobile: promo image (optional) → trust-badges card → map with 50/50 label bar below */}
      <Section className="pb-8 pt-4 md:hidden">
        {content.images?.mobile_promo && (
          <img
            src={img("mobile_promo")}
            srcSet={imgSrcSet("mobile_promo", [500, 900])}
            sizes="100vw"
            alt="ColorStone"
            loading="lazy"
            className="aspect-square w-full rounded-2xl object-cover shadow-lg"
            data-testid="mobile-promo-image"
          />
        )}
        <div className={`relative z-20 ${content.images?.mobile_promo ? "-mt-5" : ""}`}>
          <CredibilityBand />
        </div>
        <div className="relative z-10 -mt-5 overflow-hidden rounded-2xl border border-[#E2E8F0] shadow-sm">
          <SoCalMap overlays={false} />
          <div className="grid grid-cols-2 text-xs font-bold">
            <div className="flex items-center justify-center gap-1.5 border-t border-[#E2E8F0] bg-white py-2.5 text-[#0F172A]">
              <span className="h-3 w-3 rounded-sm border-2 border-[#22C55E] bg-[#22C55E]/40" /> Areas we serve
            </div>
            <div className="flex items-center justify-center gap-1.5 border-l border-t border-[#E2E8F0] bg-[#2563EB] py-2.5 text-white">
              <MapPin className="h-3.5 w-3.5" /> Southern California
            </div>
          </div>
        </div>
      </Section>

      {/* ================= HEADLINE + GALLERY ================= */}
      <Section id="gallery" className="py-14 text-center">
        <m.h2 variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} className="font-heading mx-auto max-w-3xl text-3xl font-extrabold tracking-tight text-[#0F172A] sm:text-5xl">
          Professional House Painting that looks <span className="text-[#2563EB]">great</span> & fits your budget
        </m.h2>
        <p className="mx-auto mt-4 max-w-xl text-xl text-slate-600">
          A few of the homes we've transformed across{" "}
          <strong className="font-bold text-[#0F172A]">Southern California</strong>.
          <CheckCircle2 className="ml-1 inline-block h-[1em] w-[1em] align-[-0.12em] text-[#2563EB]" />
        </p>
      </Section>
      <div className="relative overflow-hidden py-2">
        <div className={`flex w-max animate-marquee gap-4 transition-opacity duration-300 ${carouselReady ? "opacity-100" : "opacity-0"}`}>
          {marquee.map((item, i) => (
            <div key={i} className="relative shrink-0">
              <img src={item.resolved} srcSet={item.srcset} sizes="(min-width: 640px) 380px, 300px" alt={item.caption || "Painted home"} loading="eager" decoding="sync" className="h-64 w-64 rounded-xl object-cover sm:h-80 sm:w-80" />
              {item.caption && (
                <div className="absolute bottom-2 left-2 right-2 rounded-lg bg-[#0F172A]/85 px-3 py-1.5 text-sm font-semibold text-white backdrop-blur">
                  <Caption text={item.caption} />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* ================= VALUE PROP ================= */}
      <Section className="py-16 lg:py-24">
        <div className="grid gap-8 lg:grid-cols-12 lg:gap-12">
          <div className="lg:col-span-6">
            <div className="inline-flex items-center gap-2 rounded-full bg-[#0F172A] px-4 py-1.5">
              {img("logo_circle") ? (
                <img src={img("logo_circle")} alt="" className="h-5 w-5 rounded-full object-cover" />
              ) : (
                <Sparkles className="h-4 w-4 text-[#2563EB]" />
              )}
              <span className="text-sm font-bold uppercase tracking-[0.15em] text-white">Why Homeowners Choose ColorStone</span>
            </div>
            <h2 className="font-heading mt-5 text-3xl font-extrabold tracking-tight text-[#0F172A] sm:text-5xl">
              A <span className="text-[#2563EB]">better</span> painting experience from start to finish
            </h2>
            <p className="mt-5 text-xl leading-relaxed text-slate-600">
              Homeowners <span className="font-bold text-[#0F172A]">don't</span> just want a new paint color.
              They want a crew that shows up, protects their home, communicates clearly, works clean, and
              delivers a long-lasting finish that actually feels worth paying for.
            </p>
            <p className="mt-6 text-lg font-bold text-[#0F172A] sm:text-xl">
              That's what ColorStone offers:
            </p>
            <div className="mt-4 grid gap-x-6 gap-y-5 sm:grid-cols-2">
              {VALUE_PROPS.map((v, i) => {
                const Icon = v.icon;
                return (
                  <m.div key={i} variants={fadeUp} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} className="flex items-start gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#2563EB]/12">
                      <Icon className="h-5 w-5 text-[#2563EB]" />
                    </span>
                    <span className="text-base font-medium leading-snug text-[#1E293B]">{v.text}</span>
                  </m.div>
                );
              })}
            </div>
            <button onClick={openQuote} data-testid="valueprop-cta-btn" className={`mt-8 ${CTA_CLS}`}>
              Get a Free House Painting Quote <ArrowRight className="h-5 w-5 text-[#F97316]" />
            </button>
          </div>
          <div className="lg:col-span-6">
            <div className="relative">
              <img src={img("kitchen")} srcSet={imgSrcSet("kitchen")} sizes="(min-width: 1024px) 600px, 100vw" alt="Kitchen renovation & paint" loading="lazy" className="h-full max-h-[560px] w-full rounded-2xl object-cover shadow-lg" />
              <div className="absolute bottom-4 left-4 max-w-[calc(100%-2rem)] rounded-xl bg-white/95 px-3 py-2 shadow-lg backdrop-blur sm:px-4 sm:py-3">
                <div className="flex items-center gap-1 whitespace-nowrap sm:block sm:whitespace-normal">
                  <p className="text-xs font-bold text-[#0F172A] sm:text-base">Los Angeles, CA</p>
                  <p className="text-xs text-slate-500 sm:text-sm">Kitchen renovation &amp; paint</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* ================= REVIEWS ================= */}
      <section id="reviews" className="relative overflow-hidden py-16 lg:py-24">
        <img src={img("review_bg")} srcSet={imgSrcSet("review_bg")} sizes="100vw" alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[#0F172A]/92" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0F172A] via-[#0F172A]/85 to-[#0F172A]" />
        <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
          <div className="mb-12 text-center">
            <div className="flex flex-col items-center gap-4">
              {resolve(content.images?.logo_white) ? (
                <img src={resolve(content.images.logo_white)} alt="ColorStone" className="h-10 w-auto object-contain" />
              ) : null}
              <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5">
                {resolve(content.images?.verified) ? (
                  <img src={resolve(content.images.verified)} alt="Verified" className="h-12 w-auto object-contain" />
                ) : (
                  <BadgeCheck className="h-5 w-5 text-[#2563EB]" />
                )}
                <span className="text-sm font-bold uppercase leading-none tracking-[0.15em] text-white">Google Verified Business</span>
              </div>
            </div>
            <h2 className="font-heading mx-auto mt-5 max-w-2xl text-3xl font-extrabold tracking-tight text-white sm:text-5xl">
              What homeowners are saying about us
            </h2>
          </div>

          <div className="grid gap-6 md:grid-cols-3">
            {reviewCards.length > 0
              ? reviewCards.slice(0, 3).map((rc, i) => (
                  <div key={i} className="overflow-hidden rounded-2xl bg-white p-2 shadow-xl" data-testid={`review-card-${i}`}>
                    <img src={rc.src} srcSet={rc.srcset} sizes="(min-width: 768px) 400px, 90vw" alt={`Review ${i + 1}`} loading="lazy" className="aspect-square w-full rounded-xl object-cover" />
                  </div>
                ))
              : REVIEWS.map((r, i) => (
                  <m.div key={i} variants={fadeUp} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} className="flex flex-col rounded-2xl bg-white p-6 shadow-xl" data-testid={`review-card-${i}`}>
                    <div className="mb-3 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#2563EB]/15 font-heading font-black text-[#2563EB]">{r.name[0]}</span>
                        <div>
                          <p className="text-base font-bold text-[#0F172A]">{r.name}</p>
                          <p className="text-sm text-slate-400">{r.when}</p>
                        </div>
                      </div>
                      <Quote className="h-6 w-6 text-slate-200" />
                    </div>
                    <div className="mb-3 flex">
                      {[...Array(5)].map((_, j) => (<Star key={j} className="h-4 w-4 text-[#F59E0B]" fill="#F59E0B" />))}
                    </div>
                    <p className="text-base leading-relaxed text-slate-600">{r.text}</p>
                  </m.div>
                ))}
          </div>

          <div className="mt-12 text-center">
            <button onClick={openQuote} data-testid="reviews-cta-btn" className={CTA_CLS}>
              Get a Free House Painting Quote <ArrowRight className="h-5 w-5 text-[#F97316]" />
            </button>
          </div>
        </div>
      </section>

      {/* ================= SERVICES ================= */}
      <Section id="services" className="py-16 lg:py-28">
        <div className="mb-14 text-center">
          <Overline>Our Services</Overline>
          <h2 className="font-heading mx-auto mt-3 max-w-2xl text-3xl font-extrabold tracking-tight text-[#0F172A] sm:text-5xl">
            Make your home feel like <span className="text-[#2563EB]">new</span> again
          </h2>
        </div>
        <div className="space-y-24 lg:space-y-28">
          <ServiceBlock
            id="interior"
            overline="Best Interior Painting"
            title="Trusted Interior Painting for Homes & Businesses"
            titleNode={<><span className="text-[#2563EB]">Trusted Interior</span> Painting for Homes & Businesses</>}
            intro="For all kinds of interiors — ceilings, rooms, walls, furniture & surfaces. We paint, repair, and restore interior surfaces with a clean, polished finish that brightens your home and stands up to everyday living."
            features={INTERIOR}
            img={img("interior")}
            imgSrcSet={imgSrcSet("interior")}
            onQuote={openQuote}
            ctaLabel="Get a Free Interior Painting Quote"
            ctaTestId="interior-cta-btn"
          />
          <ServiceBlock
            id="exterior"
            overline="Exterior Painting"
            title="Top-Rated Residential & Commercial Exterior Painting"
            titleNode={<>Top-Rated Residential & Commercial <span className="text-[#2563EB]">Exterior Painting</span></>}
            intro="We paint, repair & apply all kinds of exterior surfaces and finishes. Get a durable paint finish that instantly boosts curb appeal and protects your home from sun, weather, and time."
            features={EXTERIOR}
            img={img("exterior")}
            imgSrcSet={imgSrcSet("exterior")}
            reverse
            onQuote={openQuote}
            ctaLabel="Get a Free Exterior Painting Quote"
            ctaTestId="exterior-cta-btn"
          />
        </div>
      </Section>

      {/* ================= COMPLIMENTARY SERVICES ================= */}
      <Section className="pb-16 lg:pb-24">
        <img
          src={img("complimentary")}
          srcSet={imgSrcSet("complimentary")}
          sizes="100vw"
          alt="Complimentary services"
          loading="lazy"
          className="aspect-square w-full rounded-2xl object-cover shadow-lg lg:hidden"
          data-testid="complimentary-image"
        />
        {/* PC-only: upper half of the image, 50% of the box width, flush on top of the box */}
        <div className="mx-auto hidden w-1/2 overflow-hidden rounded-t-2xl shadow-lg lg:block">
          <img
            src={img("complimentary")}
            srcSet={imgSrcSet("complimentary")}
            sizes="50vw"
            alt="Complimentary services"
            loading="lazy"
            className="aspect-[2/1] w-full object-cover object-top"
            data-testid="complimentary-image-pc"
          />
        </div>
        <div className="relative z-10 -mt-14 rounded-3xl bg-[#0F172A] p-4 sm:p-8 lg:mt-0 lg:p-14">
          <div className="grid gap-8 lg:grid-cols-12 lg:gap-12">
            <div className="lg:col-span-5">
              <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5">
                <img src={cslbImg} alt="CSLB Licensed" className="h-5 w-auto object-contain" />
                <span className="text-sm font-bold uppercase tracking-[0.15em] text-white">Certified Contractors</span>
              </div>
              <h3 className="font-heading mt-5 text-3xl font-extrabold tracking-tight text-white sm:text-4xl">Complimentary services</h3>
              <p className="mt-4 text-lg leading-relaxed text-slate-300">
                We can help with almost anything along the way. One trusted crew, one
                point of contact — from prep to the finishing touches.
              </p>
              <button onClick={openQuote} data-testid="comp-cta-btn" className={`mt-7 ${CTA_CLS}`}>
                Get a Free Quote <ArrowRight className="h-5 w-5 text-[#F97316]" />
              </button>
            </div>
            <div className="lg:col-span-7">
              <div className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
                {COMP_SERVICES.map((s, i) => (
                  <div key={i} className="flex items-center gap-2.5 border-b border-white/10 py-2">
                    <CheckCircle2 className="h-5 w-5 shrink-0 text-[#2563EB]" />
                    <span className="text-base font-medium text-slate-200">{s}</span>
                  </div>
                ))}
              </div>
              <p className="mt-4 text-base font-semibold text-[#2563EB]">And more!</p>
            </div>
          </div>
        </div>
      </Section>

      {/* ================= SERVICE AREA ================= */}
      <Section id="areas" className="pb-16 lg:pb-24">
        <div className="max-w-3xl">
          <Overline><MapPin className="mr-1 inline h-4 w-4" /> Service Areas</Overline>
          <h2 className="font-heading mt-3 text-3xl font-extrabold tracking-tight text-[#0F172A] sm:text-5xl">
            Serving the Greater L.A. Area, Orange County & San Bernardino County
          </h2>
          <p className="mt-4 text-xl text-slate-600">
            Type your zip code below and we'll confirm availability and send your free painting quote.
          </p>
        </div>
        <div className="mt-8 grid items-start gap-8 lg:grid-cols-2">
          <div className="max-w-md overflow-hidden rounded-2xl border-2 border-[#F97316] bg-white shadow-sm md:max-w-none">
            {/* Mobile only: map sits flush inside the top of the form card */}
            <div className="lg:hidden">
              <SoCalMap countiesLabel="Areas we serve" countiesClassName="bottom-3 left-3" countiesBare />
            </div>
            <div className="p-6">
              <QuoteForm variant="light" testPrefix="areas" />
            </div>
          </div>
          <div className="hidden overflow-hidden rounded-2xl border border-[#E2E8F0] shadow-sm lg:block">
            <SoCalMap />
          </div>
        </div>
      </Section>

      {/* ================= GALLERY ================= */}
      <Section className="pb-14 pt-4 lg:pb-20">
        <div className="mb-10 text-center">
          <Overline>Our Work</Overline>
          <h2 className="font-heading mx-auto mt-3 max-w-2xl text-3xl font-extrabold tracking-tight text-[#0F172A] sm:text-5xl">
            Real <span className="text-[#2563EB]">ColorStone</span> projects across Southern California
          </h2>
          <p className="mx-auto mt-3 flex max-w-xl items-center justify-center gap-2 text-lg font-semibold text-slate-600">
            <span><strong className="font-bold text-[#0F172A]">Scroll to the right</strong> to see the most recent transformations</span>
            <ArrowRight className="h-5 w-5 shrink-0 text-[#F97316]" />
          </p>
        </div>

        {/* Mobile: one big image + peek of the next */}
        <div className="-mx-5 flex snap-x snap-mandatory gap-4 overflow-x-auto px-5 pb-4 lg:hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {carousel.map((item, i) => (
            <div key={i} className="relative w-[82%] shrink-0 snap-center" data-testid={`gallery-m-${i}`}>
              <img src={item.resolved} srcSet={item.srcset} sizes="82vw" alt={item.caption} loading="lazy" className="aspect-square w-full rounded-2xl object-cover shadow-lg" />
              {item.caption && (
                <div className="absolute inset-x-3 bottom-3 rounded-lg bg-[#0F172A]/85 px-3 py-2 text-sm font-semibold text-white backdrop-blur">
                  <Caption text={item.caption} />
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Desktop: 3x2 horizontally-scrollable grid with a peek of the next column */}
        <div className="hidden lg:block">
          <div className="grid grid-flow-col grid-rows-2 auto-cols-[26%] gap-5 overflow-x-auto pb-4 snap-x snap-mandatory [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {carousel.map((item, i) => (
              <div key={i} className="group relative shrink-0 snap-start overflow-hidden rounded-2xl shadow-lg" data-testid={`gallery-${i}`}>
                <img src={item.resolved} srcSet={item.srcset} sizes="330px" alt={item.caption} loading="lazy" className="aspect-square w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A]/85 via-[#0F172A]/10 to-transparent" />
                {item.caption && (
                  <div className="absolute inset-x-4 bottom-4">
                    <p className="text-base font-semibold text-white"><Caption text={item.caption} /></p>
                  </div>
                )}
              </div>
            ))}
          </div>
          <p className="mt-3 text-center text-sm font-medium text-slate-400">Scroll to see more →</p>
        </div>
      </Section>

      {/* ================= FINAL CTA BAND ================= */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#0F172A] via-[#13233F] to-[#1E293B]">
        <div className="pointer-events-none absolute -right-24 top-0 h-80 w-80 rounded-full bg-[#2563EB]/25 blur-3xl" />
        <div className="pointer-events-none absolute -left-16 bottom-0 h-72 w-72 rounded-full bg-[#F97316]/12 blur-3xl" />
        <div className="relative mx-auto max-w-7xl px-5 py-20 text-center lg:px-8 lg:py-28">
          <h2 className="font-heading mx-auto max-w-3xl text-3xl font-extrabold tracking-tight text-white sm:text-5xl">
            Ready to fall in love with your home again?
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-xl text-slate-300">
            Free, no-obligation quote. Fast turnarounds. Backed by a 5-year workmanship warranty.
          </p>
          <button onClick={openQuote} data-testid="final-cta-btn" className={`mt-8 ${CTA_CLS}`}>
            Claim Your FREE Quote <ArrowRight className="h-5 w-5 text-[#F97316]" />
          </button>
        </div>
      </section>
      </main>

      <Footer />

      {/* Sticky mobile call CTA */}
      <div className="pointer-events-none fixed inset-x-0 bottom-0 z-40 p-3 lg:hidden">
        <a href={telHref} onClick={() => pushEvent("PaintingCall", { phone, location: "sticky" })} data-testid="sticky-mobile-cta" className="phoneLink pointer-events-auto flex w-full items-center justify-center gap-2 rounded-full bg-[#2563EB] py-3.5 text-lg font-bold text-white shadow-xl shadow-[#0F172A]/40">
          <Phone className="h-5 w-5" fill="white" /> Call Us for a Free Quote
        </a>
      </div>
      <div className="h-16 lg:hidden" />
    </div>
  );
}
